/**
 * Identity for the module-level "already tapped / dismissed" sets of the chip
 * resources (tool choices, action chips, follow-up suggestions).
 *
 * Those sets exist so a tap survives a re-mount within the same turn. They are
 * keyed by resource, and getting that key wrong is not cosmetic: a key that
 * repeats across turns makes the second occurrence mount as already-consumed and
 * the chips never appear — until a reload clears the module state, which is
 * exactly how the bug presented.
 *
 * `ResourceDetails` derives its `stableKey` as
 * `_stableId || reference || _id || resource-{type}-{index}`. The last branch is
 * positional, so every first tool-choice block of every message gets
 * `resource-toolChoices-0`. It is never empty either, so the `stableKey || …`
 * fallbacks the components used to have could never run.
 *
 * Hence: accept a stableKey only when it is genuinely unique, otherwise fall
 * back to the resource's own content.
 */

/** Positional keys look like `resource-toolChoices-0` — those are not identities. */
const POSITIONAL_KEY = /^resource-[A-Za-z]+-\d+$/;

export const chipResourceKey = (
  stableKey: string | undefined,
  type: string,
  parts: (string | undefined)[],
): string => {
  if (stableKey && !POSITIONAL_KEY.test(stableKey)) {
    return stableKey;
  }
  const content = parts.filter(Boolean).join('|');
  return `${type}::${content || 'empty'}`;
};
